import { ChartColumn } from "lucide-react";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";

import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardStatsBucket, CardStatsPeriod } from "../types/card-stats.types";
import {
  cardStatsLocale,
  formatSpendBucketDate,
  formatSpendBucketTick,
  isSpendTickShown,
} from "../utils/cardStatsFormat";
import { formatUSD } from "../utils/walletMovementFormat";

type CardUsageChartProps = {
  title: string;
  subtitle: string;
  data: ReadonlyArray<CardStatsBucket>;
  bucket: CardStatsPeriod["bucket"];
  /** Only shapes the first load: a refetch keeps the bars of the previous range on screen. */
  isLoading?: boolean;
  className?: string;
};

/** Where the dashed guides sit, as a share of the tallest bar. */
const GRID_STEPS = [1, 0.5, 0] as const;
const SKELETON_HEIGHTS = ["h-[45%]", "h-[70%]", "h-[35%]", "h-[60%]", "h-[85%]"] as const;

const formatAxisUSD = (raw: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(raw);

export const CardUsageChart = ({
  title,
  subtitle,
  data,
  bucket,
  isLoading = false,
  className,
}: CardUsageChartProps) => {
  const { t, activeLanguage } = useTranslations("cards");
  // The bucket the readout describes: the one under the pointer or focus, else the latest.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const lastIndex = data.length - 1;
  const shownIndex = activeIndex != null && activeIndex <= lastIndex ? activeIndex : lastIndex;
  const max = Math.max(0, ...data.map((d) => d.amount));
  const isEmpty = data.length > 0 && max === 0;

  const buckets = useMemo(() => {
    const locale = cardStatsLocale(activeLanguage);
    return data.map((d, i) => {
      const date = formatSpendBucketDate(d.date, bucket, locale);
      const label = bucket === "week" ? t("card_detail_chart_week_of", { date }) : date;
      return {
        key: d.date,
        amount: d.amount,
        tick: formatSpendBucketTick(d.date, bucket, locale),
        // The latest bucket runs up to now, so it is still filling.
        label: i === data.length - 1 ? t("card_detail_chart_in_progress", { label }) : label,
      };
    });
  }, [data, bucket, activeLanguage, t]);

  const shown = buckets[shownIndex];

  // One tab stop for the whole chart (the bar the readout shows); the arrows walk the bars. Fourteen tab stops
  // that do nothing but describe themselves were in the way of everything after the chart.
  const barRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleBarKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next =
      event.key === "ArrowRight"
        ? Math.min(index + 1, lastIndex)
        : event.key === "ArrowLeft"
          ? Math.max(index - 1, 0)
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? lastIndex
              : null;
    if (next === null) return;
    event.preventDefault();
    barRefs.current[next]?.focus();
  };

  return (
    <div className={cn("rounded-2xl border border-border bg-card p-5 shadow-sm", className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-foreground">{title}</div>
          <div className="text-[11px] text-muted-foreground">{subtitle}</div>
        </div>
        {shown && !isEmpty && (
          <div className="ml-auto text-right">
            <div className="font-mono text-lg font-semibold tabular-nums text-foreground">
              {formatUSD(shown.amount)}
            </div>
            <div className="text-[11px] text-muted-foreground">{shown.label}</div>
          </div>
        )}
      </div>

      {data.length === 0 && isLoading ? (
        <div className="flex h-40 items-end gap-2.5 pr-14" aria-hidden="true">
          {SKELETON_HEIGHTS.map((height) => (
            <ColocaSkeleton key={height} className={cn("w-full rounded-b-none", height)} />
          ))}
        </div>
      ) : data.length === 0 || isEmpty ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-sm text-muted-foreground">
          <ChartColumn className="size-5" aria-hidden="true" />
          {t("card_detail_chart_empty")}
        </div>
      ) : (
        <>
          <div className="relative h-40" onMouseLeave={() => setActiveIndex(null)}>
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              {GRID_STEPS.map((step) => (
                <div key={step} className="absolute inset-x-0" style={{ top: `${(1 - step) * 100}%` }}>
                  <div className="mr-14 border-t border-dashed border-border" />
                  <span className="absolute right-0 top-0 w-12 -translate-y-1/2 text-right font-mono text-[10px] tabular-nums text-muted-foreground">
                    {formatAxisUSD(max * step)}
                  </span>
                </div>
              ))}
            </div>
            {/* The columns stretch to the full height (no `items-end` here): a bar's `height: N%` resolves
                against its column, and a column sized to its content is 0px tall — which is how this chart
                used to render its labels and no bars at all. */}
            <div
              role="group"
              aria-label={title}
              className="absolute inset-y-0 left-0 right-14 flex gap-1.5 sm:gap-2.5"
            >
              {buckets.map((b, i) => (
                <button
                  key={b.key}
                  ref={(node) => {
                    barRefs.current[i] = node;
                  }}
                  type="button"
                  tabIndex={i === shownIndex ? 0 : -1}
                  aria-label={t("card_detail_chart_bar_aria", {
                    label: b.label,
                    amount: formatUSD(b.amount),
                  })}
                  onMouseEnter={() => setActiveIndex(i)}
                  onFocus={() => setActiveIndex(i)}
                  onBlur={() => setActiveIndex(null)}
                  onKeyDown={(event) => handleBarKeyDown(event, i)}
                  className="group flex min-w-0 flex-1 cursor-default flex-col justify-end rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {b.amount > 0 ? (
                    <span
                      className={cn(
                        "w-full rounded-t-md transition-colors",
                        i === shownIndex ? "bg-primary" : "bg-primary/25 group-hover:bg-primary/50",
                      )}
                      style={{ height: `${Math.max(2, (b.amount / max) * 100)}%` }}
                    />
                  ) : (
                    <span className="h-0.5 w-full rounded-full bg-border" />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="mr-14 mt-2 flex gap-1.5 sm:gap-2.5" aria-hidden="true">
            {buckets.map((b, i) => (
              <span
                key={b.key}
                className={cn(
                  "min-w-0 flex-1 truncate text-center font-mono text-[10px]",
                  i === lastIndex ? "font-semibold text-foreground" : "text-muted-foreground",
                )}
              >
                {isSpendTickShown(i, buckets.length) ? b.tick : ""}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
